import _ from 'lodash';

/* this function tests if an order is requesting the translation of graphics
*  either Subbed or Dedicated/Localized
*  it is mainly used to load appropriate data for orders
* */

export const isSubtitledGfxOrder = (workRequest) => {
	return _.get(workRequest, 'dng.graphics[0]', false) === 'Subtitled';
};

export const isDedicatedGfxOrder = (workRequest) => {
	return _.get(workRequest, 'dng.graphics[0]', false) === 'Dedicated / Localized';
};

export const isGfxOrder = (workRequest) => {
	return isSubtitledGfxOrder(workRequest) || isDedicatedGfxOrder(workRequest);
};

export const isGfxOnlyOrder = (workRequest) => {
	return ( isDedicatedGfxOrder(workRequest) || isSubtitledGfxOrder(workRequest) )
		&& !isDialogueOrder(workRequest) && !isNarrationOrder(workRequest);
};

/* Dedicated graphics with nothing subtitled on dialogue or narration: the only
*  translation such an order needs is the graphics one, so the vendor being
*  assigned is a graphics vendor rather than a subtitling one. Subtitled
*  graphics stay subtitling work and are deliberately excluded.
*
*  Works with both work-request objects (dng.graphics[0]) and order objects
*  (localization.graphics). The legacy string `dng` shape can never be dedicated
*  graphics, so it falls out as false on its own.
* */
export const isDedicatedGfxOnlyOrder = (dngSource) => {
	const component = (name) => _.get(dngSource, `dng.${name}[0]`) ?? _.get(dngSource, `localization.${name}`);

	return component('graphics') === 'Dedicated / Localized'
		&& component('dialogue') !== 'Subtitled'
		&& component('narration') !== 'Subtitled';
};

/* Dedicated graphics ordered alongside subtitled dialogue or narration: the
*  subtitle work and the graphics work may go to different vendors, so the order
*  form shows a translator picker per side and the work request carries a
*  `graphicsTranslator` of its own. Every other combination keeps a single
*  translator for whatever translation it needs.
*
*  Works with both work-request objects (dng.graphics[0]) and order objects
*  (localization.graphics). The legacy string `dng` shape can never be dedicated
*  graphics, so it falls out as false on its own.
* */
export const isSplitTranslatorOrder = (dngSource) => {
	// a work request stores each component as a one-item list, or — on older
	// orders — a bare string; an order carries it as a plain value
	const component = (name) => {
		const value = _.get(dngSource, `dng.${name}`);
		if( Array.isArray(value) ) {
			return value[0];
		}
		return typeof value === 'string' ? value : _.get(dngSource, `localization.${name}`);
	};

	return component('graphics') === 'Dedicated / Localized'
		&& (component('dialogue') === 'Subtitled' || component('narration') === 'Subtitled');
};

/* Whether an order asks for its graphics translation to be approved: a split
*  order has an approval choice per side (`graphicsNeedsApproval` for the
*  graphics one), every other order one choice for whatever it translates.
* */
export const graphicsNeedsApproval = (order) => (isSplitTranslatorOrder(order)
	? !!order?.localization?.graphicsNeedsApproval
	: !!order?.localization?.needsApproval);

/* this function tests if an order is requesting the translation of the dialogue
*  Subbed option for dialogue
*  it is mainly used to load appropriate data for orders
* */
export const isDialogueOrder = (workRequest) => {
	return _.get(workRequest, 'dng.dialogue[0]', false) === 'Subtitled' || workRequest.dng === 'Subtitled';
};

/* this function tests if an order is requesting the dedicated/localized translation of the dialogue
*  Dedicated/Localized option for dialogue
*  it is mainly used to load appropriate data for orders
* */
export const isDedicatedDialogueOrder = (workRequest) => {
	return _.get(workRequest, 'dng.dialogue[0]', false) === 'Dedicated / Localized';
};

/* this function tests if an order has any dialogue translation (either Subtitled or Dedicated)
*  it is used to determine if we should show dialog type in subtitler
* */
export const hasDialogueTranslation = (workRequest) => {
	return isDialogueOrder(workRequest) || isDedicatedDialogueOrder(workRequest);
};

/* this function tests if an order is requesting the translation of the narration
*  Subbed option for narration
*  it is mainly used to load appropriate data for orders
* */
export const isNarrationOrder = (workRequest) => {
	return _.get(workRequest, 'dng.narration[0]', false) === 'Subtitled';
};

/**
 * Checks whether all DNG components require no subtitler work — meaning
 * no translation is needed in the subtitler.
 *
 * Components that do NOT need subtitler work:
 * - OV: original version, nothing to translate
 * - Dedicated / Localized (dialogue/narration): dubbing handled externally
 * - None / null / undefined: component doesn't exist on asset
 *
 * Components that DO need subtitler work:
 * - Subtitled (dialogue/narration/graphics): needs translation in subtitler
 * - Dedicated / Localized (graphics only): needs graphics translation in subtitler
 *
 * Works with both work-request objects (dng.dialogue[0]) and order objects (localization.dialogue).
 */
export const isNoTranslationNeeded = (dngSource) => {
	if( !dngSource ) {
		return false;
	}

	const legacyDng = _.get(dngSource, 'dng');
	if( typeof legacyDng === 'string' ) {
		if(legacyDng === 'Subtitled') {
			return false;
		}

		if(legacyDng === 'OV' || legacyDng === 'None') {
			return true;
		}

		return false;
	}

	if( !_.has(dngSource, 'dng') && !_.has(dngSource, 'localization') ) {
		return false;
	}

	// Support work-request shape (dng.dialogue[0]) and order shape (localization.dialogue)
	const dialogue = _.get(dngSource, 'dng.dialogue[0]') ?? _.get(dngSource, 'localization.dialogue');
	const graphics = _.get(dngSource, 'dng.graphics[0]') ?? _.get(dngSource, 'localization.graphics');
	const narration = _.get(dngSource, 'dng.narration[0]') ?? _.get(dngSource, 'localization.narration');

	// Dialogue/narration only need the subtitler when "Subtitled".
	// Dedicated/Localized dialogue and narration are handled externally (dubbing),
	// OV means nothing to translate, and None/null means the component doesn't exist.
	const dialogueNeedsTranslation = dialogue === 'Subtitled';
	const narrationNeedsTranslation = narration === 'Subtitled';

	// Graphics needs the subtitler for both Subtitled and Dedicated/Localized
	// (dedicated graphics require graphics translation in the subtitler).
	const graphicsNeedsTranslation = graphics === 'Subtitled' || graphics === 'Dedicated / Localized';

	return !dialogueNeedsTranslation && !narrationNeedsTranslation && !graphicsNeedsTranslation;
};

export const isPrintOrder = (workRequest) => {
	return workRequest.assetCategory === 'print';
};

export const isPrintDateOrder = (workRequest) => {
	return workRequest.assetCategory === 'printdate';
};

export const isAutoGfxOrder = (workRequest) => workRequest.auto && !!_.get(workRequest, 'asset.transcription.autogfx', false);

export const getOrderMode = (workRequest) => {
	if(isPrintOrder(workRequest)) {
		return 'print';
	}
	if(isPrintDateOrder(workRequest)) {
		return 'printdate';
	}
	const hasDialogueOrNarration = isDialogueOrder(workRequest) || isNarrationOrder(workRequest);

	if(isGfxOrder(workRequest)) {
		if(hasDialogueOrNarration) {
			// Script Double up when dedicated graphics + any dialogue/narration to translate
			if(isDedicatedGfxOrder(workRequest)) {
				return 'script+gfx';
			}
			return 'script';
		}
		// Script Double up when both dialogue and graphics are dedicated,
		// narration is OV, and no workflow (workflow treats dedicated dialogue as OV)
		if(isDedicatedDialogueOrder(workRequest) && isDedicatedGfxOrder(workRequest)
			&& _.get(workRequest, 'dng.narration[0]') === 'OV'
			&& !workRequest?.asset?.workflow?.id) {
			return 'script+gfx';
		}
		if(isAutoGfxOrder(workRequest)) {
			return 'autogfx';
		}
		return 'gfx';
	}
	if(hasDialogueOrNarration) {
		return 'script';
	}
};

export const getTranscriptionId = (workRequest) => {
	switch(getOrderMode(workRequest)) {
		case 'script':
		case 'print':
			return workRequest.asset.transcription._id;
		case 'autogfx':
			return workRequest.asset.transcription.autogfx;
		case 'gfx':
			return workRequest.asset.transcription.graphics;
		case 'script+gfx':
			return [
				workRequest.asset.transcription.dialogue,
				workRequest.auto?workRequest.asset.transcription.autogfx:workRequest.asset.transcription.graphics
			];
		default:
			return workRequest.asset.transcription._id;
	}

};

export const getTranslationId = (workRequest) => {
	switch(getOrderMode(workRequest)) {
		case 'print':
		case 'script':
			return workRequest.translation?._id;
		case 'autogfx':
		case 'gfx':
			return workRequest.graphicsTranslation?._id;
		case 'script+gfx':
			return [
				workRequest.translation?._id,
				workRequest.graphicsTranslation?._id
			];
		default:
			return workRequest.translation?._id;
	}

};
export const getTranscriptsForOrder = (workRequest) => {
	let transcripts = {
		_id: false,
		graphics: false,
		autogfx: false,
		dialogue: false
	};
	const isPrintMode = isPrintOrder(workRequest);
	const isGfxMode = isGfxOrder(workRequest);
	const isDedicatedGfx = isDedicatedGfxOrder(workRequest);
	const isDialogueMode = isDialogueOrder(workRequest);
	const isNarrationMode = isNarrationOrder(workRequest);
	// asset.workflow can be { id: '', phase: null, definition: null } — check for a real ID
	const hasWorkflow = !!workRequest?.asset?.workflow?.id;

	const isAuto = workRequest.auto;
	const isAutosubs = workRequest.autosubs;

	if(isPrintMode) {
		transcripts._id = true;
	}
	else if(isAutosubs && !isAuto) {
		transcripts._id = true;
	}
	// automated mode options
	else if( isAuto ) {
		if(isDedicatedGfx) {
			transcripts.autogfx = true;
			if(isDialogueMode) {
				transcripts.dialogue = true;
			}
		}
		else if(isDialogueMode) {
			transcripts._id = true;
		}
	}
	else if(isGfxMode && !(isDialogueMode || isNarrationMode)) {
		if(!isNoTranslationNeeded(workRequest)) {
			// Script Double up when both dialogue and graphics are dedicated,
			// narration is OV, and no workflow (workflow treats dedicated dialogue as OV)
			const narration = _.get(workRequest, 'dng.narration[0]');
			if(isDedicatedDialogueOrder(workRequest) && isDedicatedGfx
				&& narration === 'OV' && !hasWorkflow) {
				transcripts.dialogue = true;
				transcripts.graphics = true;
			}
			else {
				transcripts.graphics = true;
			}
		}
	}
	else if((isDialogueMode || isNarrationMode) && !isGfxMode) {
		transcripts._id = true;
	}
	else if((isDialogueMode || isNarrationMode) && isGfxMode) {
		// Script Double up when dedicated graphics + any dialogue/narration to translate
		if(isDedicatedGfx) {
			transcripts.dialogue = true;
			transcripts.graphics = true;
		}
		else {
			transcripts._id = true;
		}
	}

	return transcripts;
};

/* Workflow default due dates may arrive as a Date, an ISO string, or a unix
*  timestamp (seconds) depending on the source. Normalize to a Date, or null
*  when the value is absent or unparseable.
* */
export const toWorkflowDueDate = (value) => {
	if( !value ) {
		return null;
	}

	const parsed = value instanceof Date
		? value
		: typeof value === 'number'
			? new Date(value * 1000)
			: new Date(value);

	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/* Orders must be at least 24 hours out (see ORDER_CONSTRAINTS) and the picker
*  only offers times on 30-minute steps.
* */
export const DUE_DATE_MIN_LEAD_MS = 24 * 60 * 60 * 1000;
const DUE_DATE_STEP_MS = 30 * 60 * 1000;

/* How far a date/time sits past the 30-minute mark below it. Seconds and
*  milliseconds count — 14:00:37 is no more a slot the picker renders than
*  14:12 is.
* */
const dueDateStepRemainder = (value) => ((value.getMinutes() % 30) * 60 * 1000)
	+ (value.getSeconds() * 1000)
	+ value.getMilliseconds();

/* Rolls a chosen due date/time up to the next 30-minute mark so it lines up
*  with the step increments the picker offers. Picking a date more than 24 hours
*  out keeps the current time-of-day, which rarely lands on a mark. A new Date
*  is returned so the picker's own value is never mutated underneath it; a value
*  already on a mark (or a missing one) is passed straight through.
* */
export const roundDueDateUpToStep = (value) => {
	const remainder = value && dueDateStepRemainder(value);

	if( !remainder ) {
		return value;
	}

	return new Date(value.getTime() + (DUE_DATE_STEP_MS - remainder));
};

/* The earliest due date/time a work request may be given: the first 30-minute
*  mark at or after `now + 24h`, so the boundary lines up with an option the
*  picker actually renders and is only ever rolled forward, never landing before
*  the cutoff.
* */
export const getMinimumDueDateTime = (from = new Date()) => {
	return roundDueDateUpToStep(new Date(from.getTime() + DUE_DATE_MIN_LEAD_MS));
};

/* How long until that minimum moves. The lead time is a whole number of steps,
*  so the minimum follows the wall clock's own 30-minute marks, and it moves as
*  the clock passes one rather than as it reaches it — at 14:30:00.000 the
*  earliest slot is still 14:30 tomorrow, a millisecond later it is 15:00. So
*  anything refreshing the minimum aims just past the next mark rather than
*  repeating a fixed interval from whenever it started.
* */
export const msUntilNextDueDateStep = (from = new Date()) => {
	const remainder = dueDateStepRemainder(from);

	// Reading the clock exactly on a mark is the one case where the minimum is
	// already on its way out: it holds for this single millisecond and not the
	// next, so wake up then rather than a whole step later.
	return remainder ? DUE_DATE_STEP_MS - remainder + 1 : 1;
};
